import { Tooltip } from '@material-ui/core';
import CSS from 'csstype';
import { cloneDeep, isEmpty, pick } from 'lodash-es';
import moment from 'moment';
import React from 'react';

import { getSmallSites } from '~/src/dash/api/channels/channels_methods';
import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import { HeaderBidderActiveActive, IUser } from '~/src/dash/api/console-api';
import {
  DashboardGraphID,
  IDashboardChartData,
  IDashboardChartDataKey,
  IDashboardHomeCardMetricKey,
  IDashboardHomeCardMetrics,
} from '~/src/dash/api/dashboards/home';
import { querySmallBidders } from '~/src/dash/api/filters/bidders';
import { getFilteredDemandSourcesWithBidderTypes } from '~/src/dash/api/filters/demandSources';
import { getInventoryRoutesByPublisherID, IInventoryRoute } from '~/src/dash/api/inventory_routes';
import { EActive } from '~/src/dash/api/types/active';
import { IDashboardCard } from '~/src/dash/components/cards/common';
import {
  IDashboardChartMetric,
  IDashboardSubChartMetric,
} from '~/src/dash/components/charts/common';
import { FilterPillKey, ICustomAPIFilterConfig } from '~/src/dash/components/filters/common';
import { TrendMiniIcon } from '~/src/dash/components/icons/misc-icons';
import { TKey, usePublicaTranslation } from '~/src/dash/publicaUI';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { EDateGroup } from '~/src/dash/utils/dateGroups';
import { IDateRange } from '~/src/dash/utils/dateTimeRanges';
import { checkUserInfoMasked } from '~/src/dash/utils/roles';
import { IDashboardHomeCardsConfig } from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardOverviewConfig';
import { ICSVColumn } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardOverview/CSVDownloadButton';
import { IMobileDashboardHomeCardsConfig } from '~/src/dash/views/mobile/MobileBaseView/Home/configs/overviewConfig';

// IMPORTANT!!!
// THIS FILE SHARES EXPORTS WITH MOBILE DASHBOARD
// CHECK THOROUGHLY BEFORE ALTERING

export interface IActiveFilters {
  demand: string;
  supply: string;
  device: string;
  deal: string;
  publisher: string;
  cluster: string;
  region: string;
  domain: string;
  requester: string;
  predictiveSmoothingCohorts: string;
}

export const clearedActiveFilters: IActiveFilters = {
  demand: '',
  supply: '',
  device: '',
  deal: '',
  publisher: '',
  cluster: '',
  region: '',
  domain: '',
  requester: '',
  predictiveSmoothingCohorts: '',
};

export const addDefaultSelected = (
  filterKeys: FilterPillKey[],
  updatedSelectedFilters: Record<FilterPillKey, IMenuList>,
  updatedFilters: Record<FilterPillKey, IMenuList>,
  cfg: ICustomAPIFilterConfig,
  areAllPubsActive?: boolean,
) => {
  filterKeys.forEach((key) => {
    if (key === 'Publishers' && areAllPubsActive) return;
    if (!isEmpty(cfg[key].defaultSelected)) {
      const defaultSelected = cfg[key].defaultSelected || [];
      defaultSelected.forEach((id) => {
        if (!updatedFilters[key][id].isDisabled) {
          updatedFilters[key][id].selected = true;
        }
        if (updatedSelectedFilters[key]) {
          updatedSelectedFilters[key][id] = updatedFilters[key][id];
        } else {
          updatedSelectedFilters[key] = {
            [id]: updatedFilters[key][id],
          };
        }
      });
    }
  });
};

export const updateDefaultSelectedForFilters = (
  selectedFilters: Record<string, IMenuList>,
  filtersCfg: ICustomAPIFilterConfig,
  defaultStateCfg: Record<string, any>,
) => {
  if (isEmpty(selectedFilters)) {
    Object.values(filtersCfg).forEach((filter) => {
      filter.defaultSelected = [];
    });
  } else {
    Object.keys(selectedFilters).forEach((key) => {
      if (key !== 'Timezones') {
        if (key === 'Filtered Demand Sources') {
          const isUsingBidderType = isBidderTypeSelected(
            selectedFilters['Filtered Demand Sources'],
          );
          if (isUsingBidderType) {
            defaultStateCfg.isDemandSourceUsingBidderType = true;
            const bidderIDsMap: Record<number, boolean> = {};
            Object.values(selectedFilters['Filtered Demand Sources']).forEach((item) => {
              (item.bidderIDs || []).forEach((bidderID) => {
                bidderIDsMap[bidderID] = true;
              });
            });
            filtersCfg[key].defaultGroupSelected = Object.keys(bidderIDsMap);
          } else {
            defaultStateCfg.isDemandSourceUsingBidderType = false;
          }
        }
        filtersCfg[key].defaultSelected = Object.keys(selectedFilters[key]);
      }
    });
  }
};

interface ITrendStickerProps {
  isActive: boolean;
  style?: CSS.Properties;
  onClick?: () => void;
}

export const DashboardTrendsSticker = ({ isActive, onClick, style = {} }: ITrendStickerProps) => {
  const { t } = usePublicaTranslation();
  return (
    <Tooltip title={t('DashboardTooltipComparisonData')} placement='top'>
      <div
        onClick={onClick}
        style={{
          borderRadius: '5px',
          backgroundColor: `${isActive ? publicaColors.honeydew : publicaColors.veryLightGray}`,
          padding: '0px 4px',
          display: 'flex',
          cursor: 'pointer',
          ...style,
        }}
      >
        <TrendMiniIcon color={publicaColors.darkGray} />
      </div>
    </Tooltip>
  );
};

// this function should only be used when a new graph is selected in order to update the cards
export const updateDisplayedMetricKeys = (
  availableMetricKeys: string[],
  currentDisplayedMetricKeys: IDashboardHomeCardMetricKey[],
): IDashboardHomeCardMetricKey[] => {
  const clonedAvailableKeys = [...availableMetricKeys];
  const updatedKeys = [] as IDashboardHomeCardMetricKey[];
  // find and remove duplicate metrics from the available to decrease the pool to only eligible and no repeats
  currentDisplayedMetricKeys.forEach((key) => {
    const keyIndex = clonedAvailableKeys.indexOf(key);
    if (keyIndex > -1) {
      clonedAvailableKeys.splice(keyIndex, 1);
    }
  });
  // second replace previous ineligible metrics with possible metrics from the new pool
  let i = 0;
  currentDisplayedMetricKeys.forEach((key) => {
    if (availableMetricKeys.indexOf(key) > -1) {
      updatedKeys.push(key);
    } else {
      updatedKeys.push(clonedAvailableKeys[i] as keyof IDashboardHomeCardMetrics);
      i++;
    }
  });
  // failsafe check to make sure there are 6
  if (updatedKeys.length < 6) {
    while (updatedKeys.length < 6) {
      updatedKeys.push(clonedAvailableKeys[i] as keyof IDashboardHomeCardMetrics);
      i++;
    }
  }
  return updatedKeys;
};

export const updateActiveChartMetrics = (
  displayedMetricKeys: (keyof IDashboardHomeCardMetrics)[],
  activeChartMetrics: IDashboardChartMetric[],
  cardMetricsCfg: Record<IDashboardHomeCardMetricKey, IDashboardCard>,
) => {
  const updatedActiveChartMetrics = [] as IDashboardChartMetric[];
  activeChartMetrics.forEach((metric) => {
    const metricKey = metric.key as keyof IDashboardHomeCardMetrics;
    const cardIndex = displayedMetricKeys.indexOf(metricKey);
    if (cardIndex > -1) {
      metric.chartColorIndex = cardIndex;
      updatedActiveChartMetrics.push(metric);
    }
    if (isEmpty(updatedActiveChartMetrics)) {
      const newMetricKey = displayedMetricKeys[0];
      const { label, valueType, chartType } =
        cardMetricsCfg[newMetricKey as keyof IDashboardHomeCardMetrics];
      updatedActiveChartMetrics.push({
        key: newMetricKey,
        label,
        valueType,
        chartColorIndex: 0,
        chartType,
      });
    }
  });
  return updatedActiveChartMetrics;
};

export type DashboardLiveCardGroup = 'demandSelected' | 'noDemandSelected';

export const getLiveCardGroup = (activeFilters: IActiveFilters): DashboardLiveCardGroup => {
  return !!activeFilters.demand || !!activeFilters.deal ? 'demandSelected' : 'noDemandSelected';
};

export interface IDashboardLiveCards {
  groupings: Record<DashboardLiveCardGroup, string[]>;
  superUserGroupings?: Record<DashboardLiveCardGroup, string[]>;
  samsungAdjustedMetrics?: Record<DashboardLiveCardGroup, string[]>;
}

export const extractViableMetrics = (
  cfg: IDashboardHomeCardsConfig | IMobileDashboardHomeCardsConfig,
  graphID: DashboardGraphID,
): Partial<Record<IDashboardHomeCardMetricKey, IDashboardCard>> => {
  const viableMetrics: Partial<Record<IDashboardHomeCardMetricKey, IDashboardCard>> = {} as Record<
    IDashboardHomeCardMetricKey,
    IDashboardCard
  >;
  const viableKeys = cfg.groupings[graphID];
  viableKeys.forEach((key) => {
    viableMetrics[key as IDashboardHomeCardMetricKey] =
      cfg.metrics[key as IDashboardHomeCardMetricKey];
  });
  return viableMetrics;
};

export const extractViableLiveMetrics = (cfg: IDashboardHomeCardsConfig, viableKeys: string[]) => {
  const viableMetrics = {} as Record<IDashboardHomeCardMetricKey, IDashboardCard>;
  viableKeys.forEach((key) => {
    viableMetrics[key as IDashboardHomeCardMetricKey] =
      cfg.metrics[key as IDashboardHomeCardMetricKey];
  });
  return viableMetrics;
};

export const subChartColors: Record<subChartColorKeys, string> = {
  magenta: '#D30C7B',
  olive: '#B2E061',
  blueGray: '#84BCEF',
  orange: '#FFA737',
  neonPink: '#FF66FF',
  purple: '#6F60DD',
  seaGreen: '#85FFC7',
  sienna: '#E88160',
  blue: '#5D66FA',
  red: '#FF4E29',
  cyan: '#ACF8F4',
  mustard: '#F3C628',
  coralpink: '#FB6376',
  mahogany: '#A75E6E',
  darkpeach: '#F4B583',
  forestgreen: '#40916C',
  ochre: '#E6DFA4',
  pink: '#FF9CEF',
  yellow: '#FFFD82',
  grey: '#8E9AAF',
  turquoise: '#00B7A9',
  lavender: '#DAC3E8',
  tan: '#C6AC8F',
  palepink: '#E8C2D2',
  darkblue: '#014F86',
  purpleblue: '#95619C',
  darkgrey: '#8B8C89',
};

export type subChartColorKeys =
  | 'magenta'
  | 'olive'
  | 'blueGray'
  | 'orange'
  | 'neonPink'
  | 'purple'
  | 'seaGreen'
  | 'sienna'
  | 'blue'
  | 'red'
  | 'cyan'
  | 'mustard'
  | 'coralpink'
  | 'mahogany'
  | 'darkpeach'
  | 'forestgreen'
  | 'ochre'
  | 'pink'
  | 'yellow'
  | 'grey'
  | 'turquoise'
  | 'lavender'
  | 'tan'
  | 'palepink'
  | 'darkblue'
  | 'purpleblue'
  | 'darkgrey';

const disableIfDemandSelected = (activeFilters: IActiveFilters) => {
  return !!activeFilters.demand || !!activeFilters.deal;
};

const disableIfNotDemandSource = (activeFilters: IActiveFilters) => {
  return activeFilters.demand !== 'Filtered Demand Sources';
};

const defaultNotDisabled = () => false;

export type DashboardSubChartMetrics = Record<IDashboardChartDataKey, IDashboardSubChartMetric>;

export interface IDashboardSubChart {
  title: string;
  tKey?: TKey;
  metrics: Partial<DashboardSubChartMetrics>;
  orderedMetricKeys: IDashboardChartDataKey[];
  dropdownFilter?: boolean;
}

export const keyLatencyMetrics = {
  EndpointLatency: {
    key: 'EndpointLatency',
    label: 'Endpoint Latency',
    responseKey: 'EndpointLatency',
    valueType: 'latency',
    color: subChartColors.blueGray,
    isDisabled: disableIfDemandSelected,
  },
  AuctionLatency: {
    key: 'AuctionLatency',
    label: 'Auction Latency',
    responseKey: 'AuctionLatency',
    valueType: 'latency',
    color: subChartColors.orange,
    isDisabled: disableIfDemandSelected,
  },
  BidderLatency: {
    key: 'BidderLatency',
    label: 'Bidder Latency',
    responseKey: 'BidderLatency',
    valueType: 'latency',
    color: subChartColors.magenta,
    isDisabled: defaultNotDisabled,
  },
} as Partial<DashboardSubChartMetrics>;

export const clientKeyLatencyMetrics = {
  CreationLatency: {
    key: 'CreationLatency',
    label: 'Creation Latency',
    responseKey: 'CreationLatency',
    valueType: 'latency',
    color: subChartColors.purple,
    isDisabled: disableIfNotDemandSource,
    yScale: 'small',
  },
  WaitLatency: {
    key: 'WaitLatency',
    label: 'Wait Latency',
    responseKey: 'WaitLatency',
    valueType: 'latency',
    color: subChartColors.seaGreen,
    isDisabled: disableIfNotDemandSource,
    yScale: 'small',
  },
} as Partial<DashboardSubChartMetrics>;

export const bidErrorsMetrics = {
  BidErrors: {
    key: 'BidErrors',
    label: 'Bid Errors',
    responseKey: 'BidErrors',
    valueType: 'whole_number',
    color: subChartColors.olive,
    isDisabled: defaultNotDisabled,
  },
  BidTimeouts: {
    key: 'BidTimeouts',
    label: 'Bid Timeouts',
    responseKey: 'BidTimeouts',
    valueType: 'whole_number',
    color: subChartColors.neonPink,
    isDisabled: defaultNotDisabled,
  },
} as Partial<DashboardSubChartMetrics>;

export const bidErrorsBreakdownMetrics = {
  BidBadServerResponseError: {
    key: 'BidBadServerResponseError',
    label: 'Bid Bad Server Response Error',
    responseKey: 'BidBadServerResponseError',
    valueType: 'whole_number',
    color: subChartColors.magenta,
    isDisabled: defaultNotDisabled,
  },
  BidUnknownError: {
    key: 'BidUnknownError',
    label: 'Bid Unknown Error',
    responseKey: 'BidUnknownError',
    valueType: 'whole_number',
    color: subChartColors.olive,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedDupMediafileUrl: {
    key: 'BidRejectedDupMediafileUrl',
    label: 'Bid Rejected Dup Mediafile URL',
    responseKey: 'BidRejectedDupMediafileUrl',
    valueType: 'whole_number',
    color: subChartColors.blueGray,
    isDisabled: defaultNotDisabled,
  },
  BidPriceBelowFloor: {
    key: 'BidPriceBelowFloor',
    label: 'Bid Price Below Floor',
    responseKey: 'BidPriceBelowFloor',
    valueType: 'whole_number',
    color: subChartColors.orange,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedDupAdomain: {
    key: 'BidRejectedDupAdomain',
    label: 'Bid Rejected Dup Adomain',
    responseKey: 'BidRejectedDupAdomain',
    valueType: 'whole_number',
    color: subChartColors.neonPink,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedDupMediafileUrlNormal: {
    key: 'BidRejectedDupMediafileUrlNormal',
    label: 'Bid Rejected Dup Mediafile URL Normal',
    responseKey: 'BidRejectedDupMediafileUrlNormal',
    valueType: 'whole_number',
    color: subChartColors.purple,
    isDisabled: defaultNotDisabled,
  },
  BidUnwrapEofError: {
    key: 'BidUnwrapEofError',
    label: 'Bid Unwrap EOF Error',
    responseKey: 'BidUnwrapEofError',
    valueType: 'whole_number',
    color: subChartColors.seaGreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedIabCatBlocklist: {
    key: 'BidRejectedIabCatBlocklist',
    label: 'Bid Rejected IAB Cat Blocklist',
    responseKey: 'BidRejectedIabCatBlocklist',
    valueType: 'whole_number',
    color: subChartColors.sienna,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedAboveMaxAdDuration: {
    key: 'BidRejectedAboveMaxAdDuration',
    label: 'Bid Rejected Above Max Ad Duration',
    responseKey: 'BidRejectedAboveMaxAdDuration',
    valueType: 'whole_number',
    color: subChartColors.red,
    isDisabled: defaultNotDisabled,
  },
  BidTimeoutError: {
    key: 'BidTimeoutError',
    label: 'Bid Timeout Error',
    responseKey: 'BidTimeoutError',
    valueType: 'whole_number',
    color: subChartColors.magenta,
    isDisabled: defaultNotDisabled,
  },
  BidBadInputError: {
    key: 'BidBadInputError',
    label: 'Bid Bad Input Error',
    responseKey: 'BidBadInputError',
    valueType: 'whole_number',
    color: subChartColors.cyan,
    isDisabled: defaultNotDisabled,
  },
  BidFailedToRequestBidsError: {
    key: 'BidFailedToRequestBidsError',
    label: 'Bid Failed To Request Bids Error',
    responseKey: 'BidFailedToRequestBidsError',
    valueType: 'whole_number',
    color: subChartColors.blue,
    isDisabled: defaultNotDisabled,
  },
  BidAuctionFailed: {
    key: 'BidAuctionFailed',
    label: 'Bid Auction Failed',
    responseKey: 'BidAuctionFailed',
    valueType: 'whole_number',
    color: subChartColors.olive,
    isDisabled: defaultNotDisabled,
  },
  BidRateLimitBlocked: {
    key: 'BidRateLimitBlocked',
    label: 'Bid Rate Limit Blocked',
    responseKey: 'BidRateLimitBlocked',
    valueType: 'whole_number',
    color: subChartColors.mustard,
    isDisabled: defaultNotDisabled,
  },
  BidFailedToParseResponse: {
    key: 'BidFailedToParseResponse',
    label: 'Bid Failed To Parse Response',
    responseKey: 'BidFailedToParseResponse',
    valueType: 'whole_number',
    color: subChartColors.coralpink,
    isDisabled: defaultNotDisabled,
  },
  BidConnectionError: {
    key: 'BidConnectionError',
    label: 'Bid Connection Error',
    responseKey: 'BidConnectionError',
    valueType: 'whole_number',
    color: subChartColors.red,
    isDisabled: defaultNotDisabled,
  },
  BidPanic: {
    key: 'BidPanic',
    label: 'Bid Panic',
    responseKey: 'BidPanic',
    valueType: 'whole_number',
    color: subChartColors.coralpink,
    isDisabled: defaultNotDisabled,
  },
  BidPrebidError: {
    key: 'BidPrebidError',
    label: 'Bid Prebid Error',
    responseKey: 'BidPrebidError',
    valueType: 'whole_number',
    color: subChartColors.neonPink,
    isDisabled: defaultNotDisabled,
  },
  BidEmptyResponseError: {
    key: 'BidEmptyResponseError',
    label: 'Bid Empty Response Error',
    responseKey: 'BidEmptyResponseError',
    valueType: 'whole_number',
    color: subChartColors.pink,
    isDisabled: defaultNotDisabled,
  },
  BidNoContent204Error: {
    key: 'BidNoContent204Error',
    label: 'Bid No Content 204 Error',
    responseKey: 'BidNoContent204Error',
    valueType: 'whole_number',
    color: subChartColors.purple,
    isDisabled: defaultNotDisabled,
  },
  BidNoFillError: {
    key: 'BidNoFillError',
    label: 'Bid No Fill Error',
    responseKey: 'BidNoFillError',
    valueType: 'whole_number',
    color: subChartColors.purple,
    isDisabled: defaultNotDisabled,
  },
  BidEmptyVastError: {
    key: 'BidEmptyVastError',
    label: 'Bid Empty Vast Error',
    responseKey: 'BidEmptyVastError',
    valueType: 'whole_number',
    color: subChartColors.pink,
    isDisabled: defaultNotDisabled,
  },
  BidVastValidateError: {
    key: 'BidVastValidateError',
    label: 'Bid Vast Validate Error',
    responseKey: 'BidVastValidateError',
    valueType: 'whole_number',
    color: subChartColors.purpleblue,
    isDisabled: defaultNotDisabled,
  },
  BidCachePutCacheMiss: {
    key: 'BidCachePutCacheMiss',
    label: 'Bid Cache Put Cache Miss',
    responseKey: 'BidCachePutCacheMiss',
    valueType: 'whole_number',
    color: subChartColors.lavender,
    isDisabled: defaultNotDisabled,
  },
  BidUnwrapTimeoutError: {
    key: 'BidUnwrapTimeoutError',
    label: 'Bid Unwrap Timeout Error',
    responseKey: 'BidUnwrapTimeoutError',
    valueType: 'whole_number',
    color: subChartColors.blueGray,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedUnknown: {
    key: 'BidRejectedUnknown',
    label: 'Bid Rejected Unknown',
    responseKey: 'BidRejectedUnknown',
    valueType: 'whole_number',
    color: subChartColors.blueGray,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedDuration: {
    key: 'BidRejectedDuration',
    label: 'Bid Rejected Duration',
    responseKey: 'BidRejectedDuration',
    valueType: 'whole_number',
    color: subChartColors.blue,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedDupCategory: {
    key: 'BidRejectedDupCategory',
    label: 'Bid Rejected Dup Category',
    responseKey: 'BidRejectedDupCategory',
    valueType: 'whole_number',
    color: subChartColors.blue,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedCacheId: {
    key: 'BidRejectedCacheId',
    label: 'Bid Rejected Cache ID',
    responseKey: 'BidRejectedCacheId',
    valueType: 'whole_number',
    color: subChartColors.darkblue,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedAdvertiserBlocklist: {
    key: 'BidRejectedAdvertiserBlocklist',
    label: 'Bid Rejected Advertiser Blocklist',
    responseKey: 'BidRejectedAdvertiserBlocklist',
    valueType: 'whole_number',
    color: subChartColors.blueGray,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedAdomainMissing: {
    key: 'BidRejectedAdomainMissing',
    label: 'Bid Rejected Adomain Missing',
    responseKey: 'BidRejectedAdomainMissing',
    valueType: 'whole_number',
    color: subChartColors.blue,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedBelowMinAdDuration: {
    key: 'BidRejectedBelowMinAdDuration',
    label: 'Bid Rejected Below Min Ad Duration',
    responseKey: 'BidRejectedBelowMinAdDuration',
    valueType: 'whole_number',
    color: subChartColors.blueGray,
    isDisabled: defaultNotDisabled,
  },
  BidNoMediafileSatisfySiteMaxBitrate: {
    key: 'BidNoMediafileSatisfySiteMaxBitrate',
    label: 'Bid No Mediafile Satisfy Site Max Bitrate',
    responseKey: 'BidNoMediafileSatisfySiteMaxBitrate',
    valueType: 'whole_number',
    color: subChartColors.blueGray,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedPodFloor: {
    key: 'BidRejectedPodFloor',
    label: 'Bid Rejected Pod Floor',
    responseKey: 'BidRejectedPodFloor',
    valueType: 'whole_number',
    color: subChartColors.turquoise,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedAllHouseAds: {
    key: 'BidRejectedAllHouseAds',
    label: 'Bid Rejected All House Ads',
    responseKey: 'BidRejectedAllHouseAds',
    valueType: 'whole_number',
    color: subChartColors.seaGreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedDupAdId: {
    key: 'BidRejectedDupAdId',
    label: 'Bid Rejected Dup AdID',
    responseKey: 'BidRejectedDupAdId',
    valueType: 'whole_number',
    color: subChartColors.blueGray,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedDupCreativeId: {
    key: 'BidRejectedDupCreativeId',
    label: 'Bid Rejected Dup CreativeID',
    responseKey: 'BidRejectedDupCreativeId',
    valueType: 'whole_number',
    color: subChartColors.blue,
    isDisabled: defaultNotDisabled,
  },
  BidNoMediafileSatisfyEndpointMaxBitrate: {
    key: 'BidNoMediafileSatisfyEndpointMaxBitrate',
    label: 'Bid No Mediafile Satisfy Endpoint Max Bitrate',
    responseKey: 'BidNoMediafileSatisfyEndpointMaxBitrate',
    valueType: 'whole_number',
    color: subChartColors.olive,
    isDisabled: defaultNotDisabled,
  },
  BidNoMediafileSatisfyEndpointMinBitrate: {
    key: 'BidNoMediafileSatisfyEndpointMinBitrate',
    label: 'Bid No Mediafile Satisfy Endpoint Min Bitrate',
    responseKey: 'BidNoMediafileSatisfyEndpointMinBitrate',
    valueType: 'whole_number',
    color: subChartColors.olive,
    isDisabled: defaultNotDisabled,
  },
  BidNoMediafileSatisfySiteMinBitrate: {
    key: 'BidNoMediafileSatisfySiteMinBitrate',
    label: 'Bid No Mediafile Satisfy Site Min Bitrate',
    responseKey: 'BidNoMediafileSatisfySiteMinBitrate',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedBrandSafetyRuleBlock: {
    key: 'BidRejectedBrandSafetyRuleBlock',
    label: 'Bid Rejected Brand Safety Rule Block',
    responseKey: 'BidRejectedBrandSafetyRuleBlock',
    valueType: 'whole_number',
    color: subChartColors.olive,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedMinPodSizeNotMeet: {
    key: 'BidRejectedMinPodSizeNotMeet',
    label: 'Bid Rejected Min Pod Size Not Meet',
    responseKey: 'BidRejectedMinPodSizeNotMeet',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedDupBidCatExclusion: {
    key: 'BidRejectedDupBidCatExclusion',
    label: 'Bid Rejected Dup Bid Cat Exclusion',
    responseKey: 'BidRejectedDupBidCatExclusion',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedTooManyBidCatExclusion: {
    key: 'BidRejectedTooManyBidCatExclusion',
    label: 'Bid Rejected Too Many Bid Cat Exclusion',
    responseKey: 'BidRejectedTooManyBidCatExclusion',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidPriceMissing: {
    key: 'BidPriceMissing',
    label: 'Bid Price Missing',
    responseKey: 'BidPriceMissing',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidBidderNotSharedInventory: {
    key: 'BidBidderNotSharedInventory',
    label: 'Bid Bidder Not Shared Inventory',
    responseKey: 'BidBidderNotSharedInventory',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedDupAdomainElea: {
    key: 'BidRejectedDupAdomainElea',
    label: 'Bid Rejected Dup Adomain (Elea)',
    responseKey: 'BidRejectedDupAdomainElea',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedDupAdomainClickThrough: {
    key: 'BidRejectedDupAdomainClickThrough',
    label: 'Bid Rejected Dup Adomain (Clickthrough)',
    responseKey: 'BidRejectedDupAdomainClickThrough',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedDupCategoryElea: {
    key: 'BidRejectedDupCategoryElea',
    label: 'Bid Rejected Dup Category (Elea)',
    responseKey: 'BidRejectedDupCategoryElea',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedPriceAboveErrorThreshold: {
    key: 'BidRejectedPriceAboveErrorThreshold',
    label: 'Bid Rejected Price Above Error Threshold',
    responseKey: 'BidRejectedPriceAboveErrorThreshold',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedDupCampaignId: {
    key: 'BidRejectedDupCampaignId',
    label: 'Bid Rejected Dup Campaign ID',
    responseKey: 'BidRejectedDupCampaignId',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedDupApsKey: {
    key: 'BidRejectedDupApsKey',
    label: 'Bid Rejected Dup APS Key',
    responseKey: 'BidRejectedDupApsKey',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedMediafileBlocklist: {
    key: 'BidRejectedMediafileBlocklist',
    label: 'Bid Rejected Mediafile Blocklist',
    responseKey: 'BidRejectedMediafileBlocklist',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedNotFirstSlotInPod: {
    key: 'BidRejectedNotFirstSlotInPod',
    label: 'Bid Rejected Not First Slot In Pod',
    responseKey: 'BidRejectedNotFirstSlotInPod',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedCreativeIdBlock: {
    key: 'BidRejectedCreativeIdBlock',
    label: 'Bid Rejected Creative ID Block',
    responseKey: 'BidRejectedCreativeIdBlock',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedNonsecureVast: {
    key: 'BidRejectedNonsecureVast',
    label: 'Bid Rejected Nonsecure VAST',
    responseKey: 'BidRejectedNonsecureVast',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedSlotTargetingMismatch: {
    key: 'BidRejectedSlotTargetingMismatch',
    label: 'Bid Rejected Slot Targeting Mismatch',
    responseKey: 'BidRejectedSlotTargetingMismatch',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidInvalidRequestUrl: {
    key: 'BidInvalidRequestUrl',
    label: 'Bid Invalid Request URL',
    responseKey: 'BidInvalidRequestUrl',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedCreativeBlockUnknown: {
    key: 'BidRejectedCreativeBlockUnknown',
    label: 'Bid Rejected Creative Block Unknown',
    responseKey: 'BidRejectedCreativeBlockUnknown',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedCreativeBlock404NotFound: {
    key: 'BidRejectedCreativeBlock404NotFound',
    label: 'Bid Rejected Creative Block 404 Not Found',
    responseKey: 'BidRejectedCreativeBlock404NotFound',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedCreativeBlockMismatchDuration: {
    key: 'BidRejectedCreativeBlockMismatchDuration',
    label: 'Bid Rejected Creative Block Mismatch Duration',
    responseKey: 'BidRejectedCreativeBlockMismatchDuration',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedCreativeValidationRejected: {
    key: 'BidRejectedCreativeValidationRejected',
    label: 'Bid Rejected Creative Validation Rejected',
    responseKey: 'BidRejectedCreativeValidationRejected',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedOverOrderFrequencyCap: {
    key: 'BidRejectedOverOrderFrequencyCap',
    label: 'Bid Rejected Over Order Frequency Cap',
    responseKey: 'BidRejectedOverOrderFrequencyCap',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedOverCampaignFrequencyCap: {
    key: 'BidRejectedOverCampaignFrequencyCap',
    label: 'Bid Rejected Over Campaign Frequency Cap',
    responseKey: 'BidRejectedOverCampaignFrequencyCap',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidSiteLoopError: {
    key: 'BidSiteLoopError',
    label: 'Bid Site Loop Error',
    responseKey: 'BidSiteLoopError',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedBlockedByAdvertiser: {
    key: 'BidRejectedBlockedByAdvertiser',
    label: 'Bid Rejected Blocked By Advertiser',
    responseKey: 'BidRejectedBlockedByAdvertiser',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedAboveSharedPodDuration: {
    key: 'BidRejectedAboveSharedPodDuration',
    label: 'Bid Rejected Above Shared Pod Duration',
    responseKey: 'BidRejectedAboveSharedPodDuration',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidMaxUnwrapError: {
    key: 'BidMaxUnwrapError',
    label: 'Bid Max Unwrap Error',
    responseKey: 'BidMaxUnwrapError',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedLanguageBlocklist: {
    key: 'BidRejectedLanguageBlocklist',
    label: 'Bid Rejected Language Blocklist',
    responseKey: 'BidRejectedLanguageBlocklist',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidMissingVastMeta: {
    key: 'BidMissingVastMeta',
    label: 'Bid Missing Vast Meta',
    responseKey: 'BidMissingVastMeta',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidXmlSyntaxError: {
    key: 'BidXmlSyntaxError',
    label: 'Bid XML Syntax Error',
    responseKey: 'BidXmlSyntaxError',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedAdvertiserRuleDoNotAir: {
    key: 'BidRejectedAdvertiserRuleDoNotAir',
    label: 'Bid Rejected Advertiser Rule Do Not Air',
    responseKey: 'BidRejectedAdvertiserRuleDoNotAir',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedAdvertiserRuleCompetitiveSeparationSelf: {
    key: 'BidRejectedAdvertiserRuleCompetitiveSeparationSelf',
    label: 'Bid Rejected Advertiser Rule Competitive Separation Self',
    responseKey: 'BidRejectedAdvertiserRuleCompetitiveSeparationSelf',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedAdvertiserRuleCompetitiveSeparationOther: {
    key: 'BidRejectedAdvertiserRuleCompetitiveSeparationOther',
    label: 'Bid Rejected Advertiser Rule Competitive Separation Other',
    responseKey: 'BidRejectedAdvertiserRuleCompetitiveSeparationOther',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedDupPhiloAudioIdent: {
    key: 'BidRejectedDupPhiloAudioIdent',
    label: 'Bid Rejected Dup Philo Audio Ident',
    responseKey: 'BidRejectedDupPhiloAudioIdent',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidInvalidMediafileDeliveryType: {
    key: 'BidInvalidMediafileDeliveryType',
    label: 'Bid Invalid Mediafile Delivery Type',
    responseKey: 'BidInvalidMediafileDeliveryType',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedCategoryExclusivity: {
    key: 'BidRejectedCategoryExclusivity',
    label: 'Bid Rejected Category Exclusivity',
    responseKey: 'BidRejectedCategoryExclusivity',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedCacheConnection: {
    key: 'BidRejectedCacheConnection',
    label: 'Bid Rejected Cache Connection',
    responseKey: 'BidRejectedCacheConnection',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedCacheXmlEncode: {
    key: 'BidRejectedCacheXmlEncode',
    label: 'Bid Rejected Cache XML Encode',
    responseKey: 'BidRejectedCacheXmlEncode',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedCacheStore: {
    key: 'BidRejectedCacheStore',
    label: 'Bid Rejected Cache Store',
    responseKey: 'BidRejectedCacheStore',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedEventTrackerBlocklist: {
    key: 'BidRejectedEventTrackerBlocklist',
    label: 'Bid Rejected Event Tracker Blocklist',
    responseKey: 'BidRejectedEventTrackerBlocklist',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidPriceTooHigh: {
    key: 'BidPriceTooHigh',
    label: 'Bid Price Too High',
    responseKey: 'BidPriceTooHigh',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedCreativeQualityAssurance: {
    key: 'BidRejectedCreativeQualityAssurance',
    label: 'Bid Rejected Creative Quality Assurance',
    responseKey: 'BidRejectedCreativeQualityAssurance',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedPlatformConnectionBidderSlotTargetingMismatch: {
    key: 'BidRejectedPlatformConnectionBidderSlotTargetingMismatch',
    label: 'Bid Rejected Platform Connection Bidder Slot Targeting Mismatch',
    responseKey: 'BidRejectedPlatformConnectionBidderSlotTargetingMismatch',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedBelowFloor: {
    key: 'BidRejectedBelowFloor',
    label: 'Bid Rejected Below Floor',
    responseKey: 'BidRejectedBelowFloor',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidCombineCreativeError: {
    key: 'BidCombineCreativeError',
    label: 'Bid Combine Creative Error',
    responseKey: 'BidCombineCreativeError',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidResponseEOFError: {
    key: 'BidResponseEOFError',
    label: 'Bid Response EOF Error',
    responseKey: 'BidResponseEOFError',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidConnectionResetByPeer: {
    key: 'BidConnectionResetByPeer',
    label: 'Bid Connection Reset By Peer',
    responseKey: 'BidConnectionResetByPeer',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidGzipReadError: {
    key: 'BidGzipReadError',
    label: 'Bid Gzip Read Error',
    responseKey: 'BidGzipReadError',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidUnwrapParseError: {
    key: 'BidUnwrapParseError',
    label: 'Bid Unwrap Parse Error',
    responseKey: 'BidUnwrapParseError',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidMacroReplacementError: {
    key: 'BidMacroReplacementError',
    label: 'Bid Macro Replacement Error',
    responseKey: 'BidMacroReplacementError',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidOtherUnwrapError: {
    key: 'BidOtherUnwrapError',
    label: 'Bid Other Unwrap Error',
    responseKey: 'BidOtherUnwrapError',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
  BidRejectedPrivateAuctionDealIDMismatch: {
    key: 'BidRejectedPrivateAuctionDealIDMismatch',
    label: 'Bid Rejected Private Auction Deal ID Mismatch',
    responseKey: 'BidRejectedPrivateAuctionDealIDMismatch',
    valueType: 'whole_number',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
} as Partial<DashboardSubChartMetrics>;

// smart floors ml metrics are in microseconds
export const smartFloorsMLMetrics = {
  ONNXCacheGetLatency: {
    key: 'ONNXCacheGetLatency',
    label: 'ONNX Cache Get Latency (μs)',
    responseKey: 'ONNXCacheGetLatency',
    valueType: 'microseconds',
    color: subChartColors.neonPink,
    isDisabled: defaultNotDisabled,
  },
  ONNXSessionGetQueueLatency: {
    key: 'ONNXSessionGetQueueLatency',
    label: 'ONNX Session Get Queue Latency (μs)',
    responseKey: 'ONNXSessionGetQueueLatency',
    valueType: 'microseconds',
    color: subChartColors.magenta,
    isDisabled: defaultNotDisabled,
  },
  ONNXInferenceLatency: {
    key: 'ONNXInferenceLatency',
    label: 'ONNX Inference Latency (μs)',
    responseKey: 'ONNXInferenceLatency',
    valueType: 'microseconds',
    color: subChartColors.blue,
    isDisabled: defaultNotDisabled,
  },
  ONNXTotalLatency: {
    key: 'ONNXTotalLatency',
    label: 'ONNX Total Latency (μs)',
    responseKey: 'ONNXTotalLatency',
    valueType: 'microseconds',
    color: subChartColors.forestgreen,
    isDisabled: defaultNotDisabled,
  },
} as Partial<DashboardSubChartMetrics>;

export const clientBidErrorsMetrics = {
  BidSchedulingIssues: {
    key: 'BidSchedulingIssues',
    label: 'Bid Scheduling Issues',
    responseKey: 'BidSchedulingIssues',
    valueType: 'whole_number',
    color: subChartColors.sienna,
    isDisabled: disableIfNotDemandSource,
    yScale: 'small',
  },
} as Partial<DashboardSubChartMetrics>;

export const extractDataForMultiLineChart = (data: IDashboardChartData[], metricKeys: string[]) => {
  return data.map((item) => {
    return pick(item, ['Date', ...metricKeys]);
  });
};

export const generateCustomDateRange = (numDays: number): IDateRange => {
  let defaultDateGroup;
  let dateGroupRange;
  switch (true) {
    case numDays >= 27:
      defaultDateGroup = EDateGroup.ByMonth;
      dateGroupRange = [EDateGroup.ByDay, EDateGroup.ByMonth];
      break;
    case numDays >= 6:
      defaultDateGroup = numDays >= 13 ? EDateGroup.ByWeek : EDateGroup.ByDay;
      dateGroupRange = [EDateGroup.ByDay, EDateGroup.ByWeek];
      break;
    default:
      defaultDateGroup = numDays > 0 ? EDateGroup.ByDay : EDateGroup.ByHour;
      dateGroupRange = [EDateGroup.ByHour, EDateGroup.ByDay];
      break;
  }
  return {
    key: 'Custom',
    label: 'Custom',
    shortLabel: 'Custom',
    defaultDateGroup,
    dateGroupRange,
  } as unknown as IDateRange;
};

export const generateInventoryRoutesAndDestinations = (publisherID: string, user: IUser) => {
  return getInventoryRoutesByPublisherID(publisherID).then((inventoryRoutes: IInventoryRoute[]) => {
    const routes: IMenuList = {};
    const destinations: IMenuList = {};
    for (const inventoryRoute of inventoryRoutes) {
      const siteIDs: string[] = [];
      for (const destination of inventoryRoute.Destinations) {
        // destinations without a site do not have data in our dash
        if (!destination.SiteID) continue;
        siteIDs.push(String(destination.SiteID));
        destinations[destination.ID] = {
          title: checkUserInfoMasked(user)
            ? `demo_destination_${destination.ID}`
            : destination.Name,
          selected: false,
          ID: String(destination.ID),
          siteID: destination.SiteID ? String(destination.SiteID) : '',
        };
      }
      // a route could be filled with destinations with no site
      if (siteIDs.length === 0) continue;
      routes[String(inventoryRoute.ID)] = {
        title: checkUserInfoMasked(user)
          ? `demo_inventory_route_${inventoryRoute.ID}`
          : inventoryRoute.Name,
        selected: false,
        ID: String(inventoryRoute.ID),
        siteIDs,
      };
    }
    return {
      Routes: routes,
      Destinations: destinations,
    };
  });
};

export const generateCustomCompareDates = (startDate: moment.Moment, endDate: moment.Moment) => {
  const weeksOffset = endDate.diff(startDate, 'days') > 28 ? 52 : 4;
  return {
    compStartDate: startDate.clone().subtract(weeksOffset, 'weeks'),
    compEndDate: endDate.clone().subtract(weeksOffset, 'weeks'),
  };
};

export const refreshBiddersFilter = async (
  session: ISessionState,
  user: IUserState,
  selectedBidders: IMenuList,
  siteIDs: string,
) => {
  const result = await querySmallBidders(session.publisher.ID, {
    publisher_id: session.publisher.ID,
    site_ids: siteIDs,
  });
  const biddersList: IMenuList = {};
  result.HeaderBidders.forEach((hb) => {
    const ID = hb.ID;
    const title = checkUserInfoMasked(user.user) ? `demo_bidder_${ID}` : hb.Name;
    biddersList[ID] = {
      title,
      selected: false,
      ID,
      active: hb.Active === HeaderBidderActiveActive,
    };
  });
  const refreshedSelectedBidders = {} as IMenuList;
  if (!isEmpty(selectedBidders)) {
    Object.keys(selectedBidders).forEach((id) => {
      if (biddersList[id]) {
        biddersList[id].selected = true;
        refreshedSelectedBidders[id] = biddersList[id];
      }
    });
  }
  return {
    selectedBidders: refreshedSelectedBidders,
    allBidders: biddersList,
  };
};

export const refreshDemandSourcesFilter = async (
  session: ISessionState,
  user: IUserState,
  selectedDemandSources: IMenuList,
  siteIDs: string,
  isBidderTypeDisabled?: boolean,
) => {
  const result = await getFilteredDemandSourcesWithBidderTypes(
    session,
    user,
    siteIDs,
    !!isBidderTypeDisabled,
  );
  const filteredDemandSources = result['Filtered Demand Sources'];
  const refreshedSelectedDemandSources = {} as IMenuList;
  if (!isEmpty(selectedDemandSources)) {
    Object.keys(selectedDemandSources).forEach((id) => {
      if (
        filteredDemandSources[id] &&
        !(isBidderTypeDisabled && filteredDemandSources[id].isDisabled)
      ) {
        filteredDemandSources[id].selected = true;
        refreshedSelectedDemandSources[id] = filteredDemandSources[id];
      }
    });
  }
  return {
    selectedDemandSources: refreshedSelectedDemandSources,
    allDemandSources: filteredDemandSources,
  };
};

export const isBidderTypeSelected = (selectedDemandSources: IMenuList) => {
  return Object.values(selectedDemandSources).some((demandSource) => demandSource.isCustom);
};

export const numSitesBidders = (bidderIDs: string, siteIDs: string) => {
  const bidderCount = bidderIDs.split(',').length || 1;
  const siteCount = siteIDs.split(',').length || 1;
  return bidderCount * siteCount;
};

export const generateCSVColumns = (
  metricKeys: (keyof IDashboardHomeCardMetrics)[],
  metricsConfig: Record<keyof IDashboardHomeCardMetrics, IDashboardCard>,
) => {
  const columns: ICSVColumn[] = [
    {
      id: 'Date',
      displayName: 'Date',
    },
  ];
  metricKeys.forEach((key: keyof IDashboardHomeCardMetrics) => {
    columns.push({
      id: key,
      displayName: metricsConfig[key].label,
    });
  });
  return columns;
};

const formatCSVDate = (date: string, grouping: number) => {
  if (!date) return '';
  if (grouping > 0) {
    return moment(date).utc().format('ddd MM/DD/YY');
  } else {
    return moment(date).utc().format('HH:mm (ddd MM/DD/YY)');
  }
};

export const formatDataToCSV = (
  mainData: IDashboardChartData[],
  compareData: IDashboardChartData[],
  isCompareEnabled: boolean,
  grouping: number,
) => {
  const formattedData: IDashboardChartData[] = [];
  const clonedMainData = cloneDeep(mainData);
  if (isCompareEnabled && !isEmpty(compareData)) {
    const clonedCompareData = cloneDeep(compareData);
    clonedCompareData.forEach((item) => {
      item.Date = formatCSVDate(item.Date, grouping);
      formattedData.push(item);
    });
  }
  clonedMainData.forEach((item) => {
    item.Date = formatCSVDate(item.Date, grouping);
    formattedData.push(item);
  });
  return Promise.resolve(formattedData);
};

export const generateCSVTitle = (filterParams: Record<string, string>) => {
  const paramsString: string[] = Object.entries(filterParams || {}).map(
    ([key, value]) => `${key}: ${value}`,
  );
  return paramsString.join('\u2028');
};

export const refreshChannelsFilter = async (publisherID: string, user: IUserState) => {
  // get sites
  const [small_sites, err] = await getSmallSites(publisherID);
  if (err) {
    console.error(err);
    return {} as IMenuList;
  }
  if (small_sites !== undefined) {
    const siteList: IMenuList = {};
    small_sites.forEach((site: ISmallSite) => {
      const siteName = checkUserInfoMasked(user.user) ? `demo_site_${site.ID}` : site.Name;
      siteList[site.ID] = {
        title: siteName,
        selected: false,
        ID: site.ID.toString(),
        active: site.Active === EActive.ACTIVE,
      };
    });
    return siteList;
  }
  return {} as IMenuList;
};
